import {
  FC,
  ReactNode,
  Key,
  useMemo,
  useState,
  useEffect,
  CSSProperties,
  memo,
} from 'react';
// 组件
import {
  ProSelect,
  ProInput,
  ProInputNumber,
  ProDatePicker,
  ProTimePicker,
} from '../antd';
import TextAreaRow from '../TextAreaRow';
import SuperSelect from '../SuperSelect';
import SuperSelectTable from '../SuperSelectTable';
import SimpleFormItem from './subpage/simpleFormItem';
// 方法
// 常量
import {
  BaseSimpleFormProps,
  SimpleFormItemEnum,
} from './interface';
import { SelectProps } from 'antd/lib/select';
import { InputNumberProps } from 'antd/lib/input-number';
import { NewInputProps } from '../antd/Input/input';
import { DatePickerProps, RangePickerProps } from 'antd/lib/date-picker';
import { TimePickerProps } from 'antd/lib/time-picker';
import { TextAreaRowProps } from '../TextAreaRow/interface';
import { SuperSelectTableProps } from '../SuperSelectTable/interface';
import {
  DEFAULT_DATA_FORMAT,
  DETAULT_TIME_FORMAT,
  TABLE_COLUMN_EMPTY,
} from '../constants/constant';
import { FormItemProps } from 'antd/lib/form';
import { getSelectPlaceholder } from '../utils/ruleUtils';
import { SuperSelectProps } from '../SuperSelect/interface';
import { baseRanges } from '../SearchForm/constant';

export interface BeforeSelectOption {
  /** after 表单类型 */
  afterFormItemType:
    | 'Input'
    | 'InputNumber'
    | 'Select'
    | 'SuperSelect'
    | 'SuperSelectTable'
    | 'TextAreaRow'
    | 'DatePicker'
    | 'RangePicker'
    | 'TimePicker';
  label?: ReactNode;
  value?: Key;
  disabled?: boolean;
  inputProps?: NewInputProps;
  inputNumberProps?: InputNumberProps;
  selectProps?: SelectProps<any>;
  textAreaRowProps?: TextAreaRowProps;
  datePickerProps?: DatePickerProps;
  rangePickerProps?: RangePickerProps;
  timePickerProps?: TimePickerProps;
  superSelectProps?: SuperSelectProps;
  superSelectTableProps?: SuperSelectTableProps;
}

export interface SimpleFormSelectCustomProps extends BaseSimpleFormProps {
  /** 下拉选项 */
  options?: BeforeSelectOption[];
  /** 是否默认选中第一项 */
  selectFirstValue?: boolean;
  /** select Props */
  selectProps?: SelectProps<any>;
  /** before formItem flex */
  beforeFormItemFlex?: number;
  /** after formItem flex */
  afterFormItemFlex?: number;
  /** 禁用时两个字段之间分隔符  */
  middleText?: string;
  /** before表单配置 */
  beforeFormItemProps?: FormItemProps;
  /** after表单配置 */
  afterFormItemProps?: FormItemProps;
  /** 左侧select值改变是否清空右侧表单的值 */
  emptyAfterValue?: boolean;
  /** before formItem width */
  beforeFormItemWidth?: number,
  /** 是否禁用 */
  disabled?: boolean | ((record: any) => boolean);
}

const formItemStyle: CSSProperties = {
  display: 'inline-block',
  marginBottom: 0,
  marginRight: 0,
};
const fullStyle: CSSProperties = { width: '100%' };
const SimpleFormSelectCustom: FC<SimpleFormSelectCustomProps> = memo(props => {
  const {
    form,
    selectProps,
    options = [],
    name,
    initialValue,
    beforeFormItemWidth,
    beforeFormItemFlex,
    afterFormItemFlex,
    middleText,
    disabledRender,
    beforeFormItemProps,
    afterFormItemProps,
    emptyAfterValue,
    selectFirstValue,
    disabled,
    ...restProps
  } = props;
  const [selectValue, setSelectValue] = useState<Key>(beforeFormItemProps?.initialValue);
  const beforeName: string = (beforeFormItemProps?.name
    ? beforeFormItemProps?.name
    : Array.isArray(name)
    ? name[0]
    : '') as string;
  const afterName: string = (afterFormItemProps?.name
    ? afterFormItemProps.name
    : Array.isArray(name)
    ? name[1]
    : '') as string;
  useEffect(() => {
    setTimeout(() => {
      const beforeValue = form?.getFieldValue(beforeName);
      setSelectValue(beforeValue);
    }, 200);
  }, []);
  useEffect(() => {
    if (beforeName && form)
      form.setFieldsValue?.({ [beforeName]: selectValue });
  }, [selectValue]);
  useEffect(() => {
    if (Array.isArray(initialValue) && initialValue.length && form)
      form.setFieldsValue({ [afterName]: initialValue[1] });
  }, []);
  useEffect(() => {
    const beforeValue = form?.getFieldValue(beforeName);
    if (beforeValue !== selectValue) {
      setSelectValue(beforeValue);
    }
  }, [props]);
  const selectOptions: any[] = useMemo(() => {
    return options.map(item => {
      const { label, value, disabled } = item;
      return { label, value, disabled };
    });
  }, [options]);
  const handleChange = (value: any, option: any) => {
    const prevItem = options.find(item => item.value === selectValue);
    const nextItem = options.find(item => item.value === value);
    setSelectValue(value);
    if (afterName && form) {
      if (
        !(
          !emptyAfterValue &&
          prevItem?.afterFormItemType === nextItem?.afterFormItemType
        )
      ) {
        form.setFieldsValue({ [afterName]: undefined });
      }
    }
    selectProps?.onChange?.(value, option);
  };
  const afterHTML = useMemo(() => {
    const findOption =
      options.find(item => item.value === selectValue) || options[0];
    const {
      afterFormItemType,
      inputProps,
      inputNumberProps,
      selectProps,
      superSelectProps,
      textAreaRowProps,
      datePickerProps,
      rangePickerProps,
      timePickerProps,
      superSelectTableProps,
    } = findOption;
    switch (afterFormItemType) {
      case 'Input':
        return <ProInput placeholder='搜索内容' allowClear style={fullStyle} {...inputProps} />;
      case 'InputNumber':
        return <ProInputNumber style={fullStyle} {...inputNumberProps} />;
      case 'Select':
        return <ProSelect style={fullStyle} placeholder={getSelectPlaceholder(findOption.label, selectProps)} {...selectProps} />;
      case 'SuperSelect':
        return <SuperSelect style={fullStyle} placeholder={getSelectPlaceholder(findOption.label, superSelectProps)} {...superSelectProps} />;
      case 'TextAreaRow':
        return <TextAreaRow placeholder='搜索内容' {...textAreaRowProps} />;
      case 'DatePicker':
        return <ProDatePicker style={fullStyle} {...datePickerProps} />;
      case 'RangePicker':
        return (
          <ProDatePicker.RangePicker
            style={fullStyle}
            ranges={baseRanges}
            {...rangePickerProps}
          />
        );
      case 'TimePicker':
        return (
          <ProTimePicker
            style={fullStyle}
            format={DETAULT_TIME_FORMAT}
            {...timePickerProps}
          />
        );
      case 'SuperSelectTable':
        if (!superSelectTableProps) return null;
        return (
          <SuperSelectTable
            {...superSelectTableProps}
          />
        );
      default:
        return <ProInput style={fullStyle} {...inputProps} />;
    }
  }, [selectValue]);
  const beforeFormItemStyle: CSSProperties = useMemo(() => {
    const newBeforeFormItemStyle: CSSProperties = { ...formItemStyle };
    if (beforeFormItemWidth) {
      newBeforeFormItemStyle.width = beforeFormItemWidth;
      return newBeforeFormItemStyle;
    }
    if (beforeFormItemFlex) newBeforeFormItemStyle.flex = beforeFormItemFlex;
    return newBeforeFormItemStyle;
  }, [beforeFormItemFlex, beforeFormItemWidth]);
  const afterFormItemStyle: CSSProperties = useMemo(() => {
    if (beforeFormItemWidth) {
      return {
        ...formItemStyle,
        width: `calc(100% - ${beforeFormItemWidth}px)`,
      };
    }
    return { ...formItemStyle, flex: afterFormItemFlex || 1 };
  }, [afterFormItemFlex, beforeFormItemWidth]);
  const renderText = () => {
    if (disabledRender) return disabledRender;
    let disabledText: ReactNode[] = [];
    if (beforeName && afterName && form) {
      const beforeValue = form.getFieldValue(beforeName);
      const afterValue = form.getFieldValue(afterName);
      const findItem = options.find(item => item.value === beforeValue);
      if (findItem) {
        disabledText.push(findItem.label);
        if (afterValue) {
          switch (findItem.afterFormItemType) {
            case 'DatePicker':
              disabledText.push(
                afterValue?.format?.(
                  findItem.datePickerProps?.format || DEFAULT_DATA_FORMAT,
                ) || TABLE_COLUMN_EMPTY,
              );
              break;
            case 'RangePicker':
              const afterValueStr = afterValue
                .map?.(
                  (item: any) =>
                    item?.format?.(
                      findItem.rangePickerProps?.format || DEFAULT_DATA_FORMAT,
                    ) || TABLE_COLUMN_EMPTY,
                )
                ?.join(' - ');
              disabledText.push(afterValueStr);
              break;
            case 'TimePicker':
              disabledText.push(
                afterValue?.format?.(
                  findItem.timePickerProps?.format || DETAULT_TIME_FORMAT,
                ) || TABLE_COLUMN_EMPTY,
              );
              break;
            case 'Select':
              const findAfterItem = findItem.selectProps?.options?.find(
                v => v.value === afterValue,
              );
              disabledText.push(findAfterItem?.label ?? TABLE_COLUMN_EMPTY);
              break;
            default:
              disabledText.push(afterValue);
          }
        }
      }
      return disabledText.join(middleText || ' - ');
    }
    return TABLE_COLUMN_EMPTY;
  };
  if (!Array.isArray(options) || !options.length) return null;
  
  return (
    <SimpleFormItem
      {...restProps}
    >
      {
        disabled ? renderText() : (
          <div className="yb-select-custom">
            <SimpleFormItem
              style={beforeFormItemStyle}
              className="yb-select-custom-before"
              name={beforeName}
              initialValue={initialValue?.[0]}
              {...beforeFormItemProps}
              form={form}
            >
              <ProSelect
                allowClear
                {...selectProps}
                options={selectOptions}
                onChange={handleChange}
              />
            </SimpleFormItem>
            <SimpleFormItem
              style={afterFormItemStyle}
              className="yb-select-custom-after"
              name={afterName}
              initialValue={initialValue?.[1]}
              {...afterFormItemProps}
              form={form}
            >
              {afterHTML}
            </SimpleFormItem>
          </div>
        )
      }
    </SimpleFormItem>
  );
});

SimpleFormSelectCustom.defaultProps = {
  emptyAfterValue: true,
  selectFirstValue: true,
};

SimpleFormSelectCustom.displayName = SimpleFormItemEnum.SimpleFormSelectCustom;

export default SimpleFormSelectCustom;
